import {
  Box,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { RationaleQuestions } from '../../redux-store/actions';
import {
  RationaleSectionType,
  RationaleQuestionType,
  ExcuseValues,
  Values,
  _initialExcuseValues,
  isRowApplicable,
  pickExcuseFields,
} from '../investors/excuseExclusion';
import { useFeatureToggles } from '../../utils/useDataMdms';
import { checkValidationBasedOnDate } from '../../utils/utilityFunctions';

const ExcuseExclusionTable: React.FC<{ values: ExcuseValues }> = ({ values }) => {
  return (
    <Box sx={{ width: '100%' }}>
      {values.rationaleQue.map((section: RationaleSectionType, i: number) => {
        const [applicableCol, rationaleCol, stocksCol, enclosuresCol] = section.columns;
        return (
          <Box key={i} sx={{ mb: 2, width: '100%' }}>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                      {applicableCol?.header ?? 'Please ✓ as applicable'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>
                      {rationaleCol?.header ?? 'Rationale'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                      {stocksCol?.header ?? 'Name of Stocks / Securities'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                      {enclosuresCol?.header ?? 'Mandatory Enclosures'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.questions.map((q: RationaleQuestionType) => {
                    const applicable = isRowApplicable(q, values);
                    return (
                      <TableRow key={q.id}>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {applicable ? '✓' : '--'}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{q.rationale}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {applicable ? values.excuse?.[q.stocksKey] || '--' : '--'}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{q.mandatoryEnclosures}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};

export default function ExcuseExclusion(): JSX.Element {
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const { toggler } = useFeatureToggles();
  const [rationaleJson, setRationaleJson] = React.useState<RationaleSectionType[]>([]);
  const [values, setValues] = React.useState<ExcuseValues>({
    excuse: _initialExcuseValues,
    rationaleQue: [] as unknown as RationaleSectionType[],
    saveType: 'save for later',
  });

  useEffect(() => {
    async function fetchRationaleData() {
      try {
        const res = ((await dispatch(RationaleQuestions())) ||
          []) as unknown as RationaleSectionType[];
        setRationaleJson(res);
      } catch (error) {
        console.error('Error fetching Excuse & Exclusion data:', error);
      }
    }
    fetchRationaleData();
  }, []);

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      // Excuse & Exclusion answers live directly on the application (BE contract).
      excuse: {
        ..._initialExcuseValues,
        ...pickExcuseFields(application),
      } as Values,
      rationaleQue: rationaleJson,
    }));
  }, [application, rationaleJson]);

  if (
    !checkValidationBasedOnDate(
      application?.createdAt || '',
      toggler.dateFeatureToggles.excuseExclusionValidationDate
    )
  ) {
    return <></>;
  }

  return (
    <Box
      sx={{
        bgcolor: 'white',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        borderRadius: '10px',
      }}
      mt={5}>
      <Box sx={{ p: 2 }}>
        <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
          <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main' }}>
              Excuse &amp; Exclusion
            </Typography>
          </Grid>
          <ExcuseExclusionTable values={values} />
        </Grid>
      </Box>
    </Box>
  );
}
